import { scopedThreadKey } from "@t3tools/client-runtime/environment";
import type { ScopedThreadRef } from "@t3tools/contracts";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { resolveStorage } from "./lib/storage";
import type { VmComposerExecutionMode } from "./vmSetupWorkspace";

interface VmExecutionStoreState {
  readonly modeByThreadKey: Readonly<Record<string, VmComposerExecutionMode>>;
  readonly setMode: (ref: ScopedThreadRef, mode: VmComposerExecutionMode) => void;
  readonly clear: (ref: ScopedThreadRef) => void;
}

export const useVmExecutionStore = create<VmExecutionStoreState>()(
  persist(
    (set) => ({
      modeByThreadKey: {},
      setMode: (ref, mode) =>
        set((state) => ({
          modeByThreadKey: { ...state.modeByThreadKey, [scopedThreadKey(ref)]: mode },
        })),
      clear: (ref) =>
        set((state) => {
          const key = scopedThreadKey(ref);
          if (!(key in state.modeByThreadKey)) return state;
          const { [key]: _removed, ...modeByThreadKey } = state.modeByThreadKey;
          return { modeByThreadKey };
        }),
    }),
    {
      name: "t3code:vm-execution:v1",
      storage: createJSONStorage(() =>
        resolveStorage(typeof window !== "undefined" ? window.localStorage : undefined),
      ),
      partialize: (state) => ({ modeByThreadKey: state.modeByThreadKey }),
    },
  ),
);

export function selectVmExecutionMode(
  state: VmExecutionStoreState,
  ref: ScopedThreadRef | null,
): VmComposerExecutionMode {
  return ref ? (state.modeByThreadKey[scopedThreadKey(ref)] ?? "host") : "host";
}
